import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Typography as MUITypography,
  styled,
  Autocomplete,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'
import { DataGrid, gridClasses, esES } from '@mui/x-data-grid'
import { grey } from '@mui/material/colors'
import AddLinkIcon from '@mui/icons-material/AddLink'
import PeopleIcon from '@mui/icons-material/People'
import LocationCityIcon from '@mui/icons-material/LocationCity'
import { getUserAll } from '../../../services'
import {
  fetchAllAssignments,
  fetchAssignmentsByUser,
  removeAssignment,
  setTenantPrimary,
  updateAssignmentPermissions,
} from '../../../services/userTenantService'
import { setIsStored } from '../../../redux/slices/Alerts'
import { getAssignmentAccessMode } from '../../../utils/treePermissions'
import { getColumns, permissionColumns } from './columns'
import AssignModal from './AssignModal'
import EditPermissionsDialog from './EditPermissionsDialog'
import AlertCustom from '../../Alerts'

const styleContainer = {
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
}

const styleDefault = theme => ({
  color: theme.palette.primary.main,
  border: `1px solid ${theme.palette.primary.main}`,
  fontSize: '12px',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  '&:active': { boxShadow: 'none', opacity: 0.9 },
})

const styleHeaderTab = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignContent: 'center',
  alignItems: 'baseline',
  ml: 4,
  py: 2,
}

const Typography = styled(MUITypography)(props => ({
  fontFamily: props.theme.typography.fontFamily,
  fontStyle: 'normal',
  fontSize: '1em',
  fontWeight: 650,
  lineHeight: '18px',
  color: '#6A6A6A',
  textAlign: 'center',
}))

// Columnas para la tabla general (todas las asignaciones)
const overviewColumns = [
  {
    field: 'userName',
    headerName: 'Usuario',
    headerAlign: 'center',
    flex: 2,
    minWidth: 150,
  },
  {
    field: 'userEmail',
    headerName: 'Email',
    headerAlign: 'center',
    flex: 2,
    minWidth: 180,
  },
  {
    field: 'tenantName',
    headerName: 'Tenant',
    headerAlign: 'center',
    flex: 1.5,
    minWidth: 120,
  },
  {
    field: 'cantonName',
    headerName: 'Canton',
    headerAlign: 'center',
    flex: 1.5,
    minWidth: 100,
    renderCell: params => params.value || '—',
  },
  {
    field: 'isPrimary',
    headerName: 'Principal',
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 90,
    renderCell: params =>
      params.value ? <Chip label="Si" color="primary" size="small" /> : null,
  },
  ...permissionColumns,
]

const PERMISSIONS_UPDATED_MESSAGE = 'Permisos actualizados correctamente'

// Fila de la tabla con el modo de permisos que corresponde al rol del usuario asignado.
const withAccessMode = (assignment, userRole) => ({
  ...assignment,
  accessMode: getAssignmentAccessMode(userRole),
})

const UserTenantTable = () => {
  const modalRef = React.useRef()
  const alertRef = React.useRef()
  const dispatch = useDispatch()
  const selectedUserRef = React.useRef(null)

  const [users, setUsers] = React.useState([])
  const [selectedUser, setSelectedUser] = React.useState(null)
  const [assignments, setAssignments] = React.useState([])
  const [selectedUserRole, setSelectedUserRole] = React.useState(null)
  const [pageSize, setPageSize] = React.useState(10)
  const [loading, setLoading] = React.useState(false)

  // Overview data (when no user selected)
  const [allAssignments, setAllAssignments] = React.useState([])
  const [summary, setSummary] = React.useState([])
  const [overviewLoading, setOverviewLoading] = React.useState(false)
  const [activeFilter, setActiveFilter] = React.useState(null)

  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const [rowToDelete, setRowToDelete] = React.useState(null)

  // Estado del diálogo de permisos
  const [permissionsRow, setPermissionsRow] = React.useState(null)
  const [savingPermissions, setSavingPermissions] = React.useState(false)

  const { isStored } = useSelector(state => state.alertsGlobal)
  const TOKEN = `Bearer ${window.localStorage.getItem('ArbolEc_token')}`

  // Keep ref in sync
  React.useEffect(() => {
    selectedUserRef.current = selectedUser
  }, [selectedUser])

  // Load overview data
  const loadOverview = React.useCallback(async () => {
    setOverviewLoading(true)
    try {
      const data = await fetchAllAssignments()
      setAllAssignments(data.assignments || [])
      setSummary(data.summary || [])
    } catch (err) {
      console.error('Error loading overview:', err)
    } finally {
      setOverviewLoading(false)
    }
  }, [])

  React.useEffect(() => {
    loadOverview()
  }, [loadOverview])

  // Load users
  React.useEffect(() => {
    getUserAll(TOKEN)
      .then(data => {
        if (data?.length) setUsers(data)
      })
      .catch(err => console.error('Error loading users:', err))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadAssignments = React.useCallback(async userId => {
    if (!userId) {
      setAssignments([])
      setSelectedUserRole(null)
      return
    }
    setLoading(true)
    try {
      const { assignments: userAssignments, userRole } = await fetchAssignmentsByUser(userId)
      setAssignments(userAssignments)
      setSelectedUserRole(userRole)
    } catch (err) {
      console.error('Error loading assignments:', err)
      setAssignments([])
      setSelectedUserRole(null)
    } finally {
      setLoading(false)
    }
  }, [])

  // Load assignments when user changes
  React.useEffect(() => {
    loadAssignments(selectedUser?.id)
  }, [selectedUser, loadAssignments])

  // Handle set primary
  const handleSetPrimary = React.useCallback(
    async row => {
      const user = selectedUserRef.current
      if (!user) return
      setLoading(true)
      const result = await setTenantPrimary(user.id, row.tenantId)
      if (result.success) {
        dispatch(setIsStored({ stored: true, message: 'Tenant principal actualizado' }))
        await loadAssignments(user.id)
        loadOverview()
      } else {
        dispatch(setIsStored({ stored: false, message: result.message }))
      }
      setLoading(false)
    },
    [loadAssignments, loadOverview, dispatch],
  )

  // Handle delete
  const handleDeleteClick = React.useCallback(row => {
    setRowToDelete(row)
    setDeleteDialogOpen(true)
  }, [])

  const confirmDelete = async () => {
    const user = selectedUserRef.current
    if (!rowToDelete || !user) return
    setLoading(true)
    setDeleteDialogOpen(false)
    const result = await removeAssignment(user.id, rowToDelete.tenantId)
    if (result.success) {
      dispatch(
        setIsStored({ stored: true, message: 'Asignacion eliminada correctamente' }),
      )
      await loadAssignments(user.id)
      loadOverview()
    } else {
      dispatch(setIsStored({ stored: false, message: result.message }))
    }
    setLoading(false)
    setRowToDelete(null)
  }

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false)
    setRowToDelete(null)
  }

  // Editar permisos de una asignación
  const handleEditPermissionsClick = React.useCallback(row => {
    setPermissionsRow(row)
  }, [])

  const handleCancelPermissions = () => setPermissionsRow(null)

  const handleSavePermissions = async permissions => {
    const user = selectedUserRef.current
    if (!permissionsRow || !user) return
    setSavingPermissions(true)
    const result = await updateAssignmentPermissions(user.id, permissionsRow.tenantId, permissions)
    setSavingPermissions(false)
    if (!result.success) {
      // El diálogo sigue abierto para reintentar o cancelar.
      dispatch(setIsStored({ stored: false, message: result.message }))
      return
    }
    setPermissionsRow(null)
    dispatch(setIsStored({ stored: true, message: result.message || PERMISSIONS_UPDATED_MESSAGE }))
    await loadAssignments(user.id)
    loadOverview()
  }

  const columns = React.useMemo(
    () => getColumns(handleSetPrimary, handleDeleteClick, handleEditPermissionsClick),
    [handleSetPrimary, handleDeleteClick, handleEditPermissionsClick],
  )

  const assignedTenantIds = React.useMemo(
    () => assignments.map(a => a.tenantId),
    [assignments],
  )

  const filteredOverview = React.useMemo(
    () =>
      activeFilter === null
        ? allAssignments
        : allAssignments.filter(a => a.tenantId === activeFilter),
    [allAssignments, activeFilter],
  )

  // El rol llega en la raíz de la respuesta del usuario y en cada fila de la vista general.
  const selectedUserAccessMode = getAssignmentAccessMode(selectedUserRole)

  const assignmentRows = React.useMemo(
    () => assignments.map(assignment => withAccessMode(assignment, selectedUserRole)),
    [assignments, selectedUserRole],
  )

  const overviewRows = React.useMemo(
    () => filteredOverview.map(assignment => withAccessMode(assignment, assignment.userRole)),
    [filteredOverview],
  )

  const handleCardFilter = tenantId => {
    setActiveFilter(prev => (prev === tenantId ? null : tenantId))
  }

  // Callback from AssignModal
  const handleAssignSuccess = React.useCallback(
    (message, success = true) => {
      dispatch(setIsStored({ stored: success, message }))
      const user = selectedUserRef.current
      if (success && user) {
        loadAssignments(user.id)
        loadOverview()
      }
    },
    [dispatch, loadAssignments, loadOverview],
  )

  React.useEffect(() => {
    if (!isStored) return
    alertRef?.current?.openAlert(true)
  }, [isStored])

  return (
    <Box sx={styleContainer}>
      <Box sx={styleHeaderTab}>
        <Typography>Administrar Asignaciones de Tenants</Typography>
      </Box>

      {/* User selector + Assign button */}
      <Box sx={{ display: 'flex', gap: 2, mx: 4, mb: 2, alignItems: 'center' }}>
        <Autocomplete
          options={users}
          getOptionLabel={option =>
            `${option.nombre} ${option.apellido} (${option.email})`
          }
          value={selectedUser}
          onChange={(_, value) => setSelectedUser(value)}
          renderInput={params => (
            <TextField
              {...params}
              label="Seleccionar usuario"
              variant="outlined"
              size="small"
            />
          )}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          sx={{ flex: 1, maxWidth: 500 }}
          noOptionsText="No se encontraron usuarios"
        />
        {selectedUser && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddLinkIcon />}
            sx={styleDefault}
            onClick={() => modalRef.current?.openModal()}>
            Asignar Tenant
          </Button>
        )}
      </Box>

      {/* Content area */}
      {selectedUser ? (
        /* --- Vista individual: asignaciones del usuario seleccionado --- */
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', px: 2 }}>
          <DataGrid
            rows={assignmentRows}
            columns={columns}
            pageSize={pageSize}
            loading={loading}
            getRowId={row => row.id || `${row.tenantId}`}
            rowsPerPageOptions={[10, 25, 50]}
            onPageSizeChange={newPageSize => setPageSize(newPageSize)}
            localeText={esES.components.MuiDataGrid.defaultProps.localeText}
            getRowSpacing={params => ({
              top: params.isFirstVisible ? 0 : 5,
              bottom: params.isLastVisible ? 0 : 5,
            })}
            sx={{
              height: '100%',
              ml: 2,
              [`& .${gridClasses.row}`]: { bgcolor: grey[100] },
            }}
            disableSelectionOnClick
          />
        </Box>
      ) : (
        /* --- Vista general: cards + tabla de todas las asignaciones --- */
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            px: 2,
          }}>
          {/* Cards resumen por tenant */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5, ml: 2 }}>
            {/* Card total */}
            <Card
              variant="outlined"
              onClick={() => setActiveFilter(null)}
              sx={{
                cursor: 'pointer',
                borderLeft: '4px solid #4E6A5B',
                boxShadow: activeFilter === null ? 'inset 0 0 0 2px #4E6A5B' : 'none',
                bgcolor: activeFilter === null ? '#4E6A5B18' : 'background.paper',
                transition: 'background-color 0.15s',
                '&:hover': { bgcolor: '#4E6A5B12' },
              }}>
              <CardContent sx={{ py: 0.75, px: 1.5, '&:last-child': { pb: 0.75 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <PeopleIcon sx={{ fontSize: 16, color: '#4E6A5B' }} />
                <MUITypography variant="caption" color="text.secondary">
                  Total
                </MUITypography>
                <MUITypography variant="subtitle2" fontWeight={700} color="#4E6A5B">
                  {allAssignments.length}
                </MUITypography>
              </CardContent>
            </Card>

            {summary.map(s => {
              const isActive = activeFilter === s.tenantId
              return (
                <Card
                  key={s.tenantId}
                  variant="outlined"
                  onClick={() => handleCardFilter(s.tenantId)}
                  sx={{
                    cursor: 'pointer',
                    borderLeft: '4px solid #1976d2',
                    boxShadow: isActive ? 'inset 0 0 0 2px #1976d2' : 'none',
                    bgcolor: isActive ? '#1976d218' : 'background.paper',
                    transition: 'background-color 0.15s',
                    '&:hover': { bgcolor: '#1976d212' },
                  }}>
                  <CardContent sx={{ py: 0.75, px: 1.5, '&:last-child': { pb: 0.75 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <LocationCityIcon sx={{ fontSize: 16, color: '#1976d2' }} />
                    <Box>
                      <MUITypography variant="caption" color="text.secondary" display="block">
                        {s.tenantName}
                      </MUITypography>
                      {s.cantonName && (
                        <MUITypography variant="caption" color="text.disabled" display="block" sx={{ lineHeight: 1.2 }}>
                          {s.cantonName}
                        </MUITypography>
                      )}
                    </Box>
                    <MUITypography variant="subtitle2" fontWeight={700} color="#1976d2" sx={{ ml: 'auto', pl: 1 }}>
                      {s.userCount}
                    </MUITypography>
                  </CardContent>
                </Card>
              )
            })}
          </Box>

          {/* Tabla general de todas las asignaciones */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <DataGrid
              rows={overviewRows}
              columns={overviewColumns}
              pageSize={pageSize}
              loading={overviewLoading}
              getRowId={row => row.id}
              rowsPerPageOptions={[10, 25, 50]}
              onPageSizeChange={newPageSize => setPageSize(newPageSize)}
              localeText={esES.components.MuiDataGrid.defaultProps.localeText}
              getRowSpacing={params => ({
                top: params.isFirstVisible ? 0 : 5,
                bottom: params.isLastVisible ? 0 : 5,
              })}
              sx={{
                height: '100%',
                ml: 2,
                [`& .${gridClasses.row}`]: { bgcolor: grey[100] },
              }}
              disableSelectionOnClick
            />
          </Box>
        </Box>
      )}

      {/* Assign Modal */}
      <AssignModal
        ref={modalRef}
        userId={selectedUser?.id}
        assignedTenantIds={assignedTenantIds}
        accessMode={selectedUserAccessMode}
        onSuccess={handleAssignSuccess}
      />

      {/* Diálogo de permisos de la asignación */}
      <EditPermissionsDialog
        open={Boolean(permissionsRow)}
        assignment={permissionsRow}
        saving={savingPermissions}
        onCancel={handleCancelPermissions}
        onSave={handleSavePermissions}
      />

      {/* Alert */}
      <AlertCustom
        ref={alertRef}
        severity={isStored?.stored !== true ? 'warning' : 'success'}>
        {isStored?.message ||
          (isStored?.stored !== true ? 'Error desconocido' : 'Guardado exitosamente.')}
      </AlertCustom>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title">
        <DialogTitle id="delete-dialog-title">{'Estas seguro?'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {rowToDelete
              ? `Se eliminara la asignacion del tenant "${rowToDelete.tenantName}". Esta accion no se puede deshacer.`
              : 'Confirmar eliminacion'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancelar</Button>
          <Button onClick={confirmDelete} color="error" autoFocus>
            Si, eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default UserTenantTable
